// [NEW FILE] Auto-saved form drafts.
//
// Problem: a user is typing in a form (self assessment, goal, employee...) and the power goes off,
// the browser closes, or they leave for a meeting. When they come back, everything is lost.
//
// Solution: while the form is open, every change is saved in this browser (about half a second after
// the user stops typing, and immediately when the tab is hidden or closed). Two copies are written:
//   - localStorage: read instantly when the form opens;
//   - IndexedDB with "strict" durability: forced to disk at once. Chrome writes localStorage to disk
//     only every few seconds, so after a real power cut the IndexedDB copy can be the newer one.
// When the form opens, the newer of the two copies is used.
// When the same form is opened again, the saved values are put back and a notice is shown with a
// "Discard" button. The draft is removed when the form is saved successfully (call draft.clear()),
// when the user discards it, after DRAFT_TTL_DAYS days, and when the user signs out.
//
// Usage (simple form):
//   const draft = useFormDraft(`goal:${goal?.id || "new"}`, form, setForm, { active: open });
//   ...after a successful save: draft.clear();
//   ...in the JSX: <DraftNotice draft={draft} />
//
// Rules:
// - `key` must be unique per form AND per record (e.g. include the goal / employee / cycle id).
// - `active` must be true only while the form is open and its initial values are loaded,
//   and false when the form is read-only (nothing to save).
// - `setValue` is called with a plain object (never an updater function).
// - Files (File/Blob) are never saved. Passwords must never be passed to this hook.
import { useCallback, useEffect, useRef, useState } from "react";

const PREFIX = "pms_draft:v1:";
export const DRAFT_TTL_DAYS = 7;
const TTL_MS = DRAFT_TTL_DAYS * 24 * 60 * 60 * 1000;
const SAVE_DELAY_MS = 500;

// Drafts are stored per signed-in user, so two people sharing a computer do not see each other's drafts.
function currentUserId() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return user?.employeeId || user?.id || "anonymous";
  } catch {
    return "anonymous";
  }
}
const storageKey = (key) => `${PREFIX}${currentUserId()}:${key}`;

// Drop File/Blob values and the keys listed in `omit` (top level) before saving or comparing.
function serialize(value, omit) {
  return JSON.stringify(value, function replacer(k, v) {
    if (this === value && omit.includes(k)) return undefined;
    if (typeof Blob !== "undefined" && v instanceof Blob) return undefined;
    return v;
  });
}

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
// Saved values win; keys that exist only in the fresh form (e.g. a new field) keep their default.
function deepMerge(base, saved) {
  if (!isPlainObject(base) || !isPlainObject(saved)) return saved;
  const out = { ...base };
  Object.entries(saved).forEach(([k, v]) => { out[k] = k in base ? deepMerge(base[k], v) : v; });
  return out;
}

// ---- IndexedDB copy (power-cut safe) ----
const IDB_NAME = "pms_drafts";
const IDB_STORE = "drafts";
let idbPromise = null;
function idb() {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  if (!idbPromise) {
    idbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(IDB_NAME, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }
  return idbPromise;
}
async function idbRun(mode, fn) {
  const db = await idb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      // "strict" = the browser confirms the write only after it is flushed to disk (where supported).
      const tx = db.transaction(IDB_STORE, mode, mode === "readwrite" ? { durability: "strict" } : undefined);
      const result = fn(tx.objectStore(IDB_STORE));
      tx.oncomplete = () => resolve(result?.result ?? null);
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}
const idbPut = (fullKey, draft) => idbRun("readwrite", (s) => s.put(draft, fullKey));
const idbGet = (fullKey) => idbRun("readonly", (s) => s.get(fullKey));
const idbDelete = (fullKey) => idbRun("readwrite", (s) => s.delete(fullKey));
const idbClear = () => idbRun("readwrite", (s) => s.clear());
async function idbDeleteExpired() {
  return idbRun("readwrite", (s) => {
    const req = s.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      if (!cursor.value?.savedAt || Date.now() - cursor.value.savedAt > TTL_MS) cursor.delete();
      cursor.continue();
    };
    return null;
  });
}

function readDraft(key) {
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft?.savedAt || Date.now() - draft.savedAt > TTL_MS) {
      localStorage.removeItem(storageKey(key));
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}
function writeDraft(key, json) {
  const draft = { savedAt: Date.now(), data: JSON.parse(json) };
  try {
    localStorage.setItem(storageKey(key), JSON.stringify(draft));
  } catch {
    // Storage full or blocked (private mode): the IndexedDB copy may still work.
  }
  idbPut(storageKey(key), draft);
}
function removeDraft(key) {
  try { localStorage.removeItem(storageKey(key)); } catch { /* ignore */ }
  idbDelete(storageKey(key));
}

// Called on sign-out (RoleContext) and once at start-up to delete expired drafts.
export function clearAllDrafts({ expiredOnly = false } = {}) {
  try {
    Object.keys(localStorage).filter((k) => k.startsWith(PREFIX)).forEach((k) => {
      if (!expiredOnly) { localStorage.removeItem(k); return; }
      try {
        const d = JSON.parse(localStorage.getItem(k));
        if (!d?.savedAt || Date.now() - d.savedAt > TTL_MS) localStorage.removeItem(k);
      } catch { localStorage.removeItem(k); }
    });
  } catch { /* ignore */ }
  if (expiredOnly) idbDeleteExpired(); else idbClear();
}
clearAllDrafts({ expiredOnly: true });

/**
 * @returns {{ restoredAt: number|null, savedAt: number|null, clear: () => void, discard: () => void }}
 */
export default function useFormDraft(key, value, setValue, { active = true, omit = [] } = {}) {
  const [stage, setStage] = useState({ key: null, step: "idle", n: 0 });
  const [restoredAt, setRestoredAt] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const baseline = useRef(null);   // JSON of the form as it was before the user typed anything
  const initial = useRef(null);    // the form's own initial value (used by "Discard")
  const pending = useRef(null);    // JSON waiting for the debounce timer
  const timer = useRef(null);
  const stageRef = useRef(stage);
  stageRef.current = stage;
  const omitRef = useRef(omit);
  omitRef.current = omit;
  const valueRef = useRef(value);
  valueRef.current = value;

  const flush = useCallback(() => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    if (pending.current !== null && stage.key) {
      writeDraft(stage.key, pending.current);
      setSavedAt(Date.now());
      pending.current = null;
    }
  }, [stage.key]);

  // 1. When the form becomes active, wait one render so the form's own initial values are in `value`.
  useEffect(() => {
    if (active && key) {
      setStage((s) => ({ key, step: "init", n: s.n + 1 }));
    } else {
      setStage((s) => (s.step === "idle" ? s : { key: null, step: "idle", n: s.n + 1 }));
      setRestoredAt(null);
      setSavedAt(null);
    }
  }, [active, key]);

  // 2. Restore a saved draft (once), then 3. keep saving every change.
  useEffect(() => {
    if (stage.step === "idle" || !active || stage.key !== key) return; // stale stage from the previous render
    const json = serialize(value, omitRef.current);
    if (stage.step === "init") {
      initial.current = value;
      baseline.current = json;
      const draft = readDraft(stage.key);
      if (draft) {
        const merged = deepMerge(value, draft.data);
        if (serialize(merged, omitRef.current) !== json) {
          setValue(merged);
          setRestoredAt(draft.savedAt);
          setSavedAt(draft.savedAt);
        } else {
          removeDraft(stage.key); // nothing different from what the page already shows
        }
      }
      // The IndexedDB copy arrives a moment later. Use it only if it is newer than the localStorage copy
      // (power cut case) and the user has not started typing in the meantime.
      const keyAtStart = stage.key;
      const n = stage.n;
      idbGet(storageKey(keyAtStart)).then((saved) => {
        if (!saved?.data || !saved.savedAt || Date.now() - saved.savedAt > TTL_MS) return;
        if (draft && saved.savedAt <= draft.savedAt) return;
        if (stageRef.current.n !== n || stageRef.current.key !== keyAtStart) return; // form closed or re-opened
        if (serialize(valueRef.current, omitRef.current) !== baseline.current) return; // user already typed
        const merged = deepMerge(initial.current, saved.data);
        if (serialize(merged, omitRef.current) === baseline.current) return;
        setValue(merged);
        setRestoredAt(saved.savedAt);
        setSavedAt(saved.savedAt);
      });
      setStage((s) => ({ ...s, step: "track" }));
      return;
    }
    // step === "track"
    if (json === baseline.current) {
      if (timer.current) window.clearTimeout(timer.current);
      pending.current = null;
      removeDraft(stage.key);
      setSavedAt(null);
      return;
    }
    pending.current = json;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(flush, SAVE_DELAY_MS);
  }, [value, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save immediately when the tab is hidden, reloaded or closed (power cut protection is best-effort:
  // the debounce is short, so at most the last half second of typing can be lost).
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      flush(); // the form is closing or the page is changing: keep what was typed
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [flush]);

  // Call after a successful save/submit. The current values become the new baseline.
  const clear = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    pending.current = null;
    if (stage.key) removeDraft(stage.key);
    else if (key) removeDraft(key);
    setRestoredAt(null);
    setSavedAt(null);
    setStage((s) => (s.step === "idle" ? s : { ...s, step: "init", n: s.n + 1 }));
  }, [stage.key, key]);

  // "Discard" on the notice: go back to the values the form had before the draft was restored.
  const discard = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    pending.current = null;
    if (stage.key) removeDraft(stage.key);
    setRestoredAt(null);
    setSavedAt(null);
    if (initial.current !== null) setValue(initial.current);
  }, [stage.key, setValue]);

  return { restoredAt, savedAt, clear, discard };
}
